/**
 * Centralized PostHog analytics event tracking
 *
 * Event names are stored as constants to ensure consistency across the application
 * and prevent typos when tracking events.
 */

/**
 * Event context for tracking where and how items are displayed
 * Used for CTR measurement and recommendation attribution
 */
export interface EventContext {
    // Where the item was displayed
    feed_type?: 'following' | 'discover' | 'search' | 'none'
    page_type?: 'home' | 'collection' | 'profile' | 'product' | 'search'

    // Recommendation attribution (if applicable)
    recommendation_strategy?:
        | 'collection-based'
        | 'brand-affinity'
        | 'price-range'
        | 'search-based'
        | 'collaborative'
        | 'content-based'
        | 'hybrid'
        | 'none'
    recommendation_id?: string // For A/B testing

    // Additional context
    collection_id?: string // If displayed in a collection
    search_query?: string // If from search results
    profile_id?: string // If displayed on a profile
    position?: number // Position in feed/list (for simple feeds)
    feed_item_index?: number // Index of the feed item (e.g., which activity in following feed)
    activity_item_index?: number // Index of item within the feed item (e.g., which product in the activity)
    section?: string // UI section (e.g., "similar_products", "you_might_like")
}

/**
 * Analytics event names
 * Use these constants when tracking events with PostHog
 */
export const AnalyticsEvents = {
    // Onboarding & Auth
    ONBOARDING_STARTED: 'onboarding_started',
    ONBOARDING_COMPLETED: 'onboarding_completed',
    AUTH_MODAL_OPENED: 'auth_modal_opened',
    AUTH_MODAL_LOGIN_CLICKED: 'auth_modal_login_clicked',
    AUTH_MODAL_SIGNUP_CLICKED: 'auth_modal_signup_clicked',

    // Try-On
    TRY_ON_STARTED: 'try_on_started',
    TRY_ON_GENERATION_STARTED: 'try_on_generation_started',
    TRY_ON_COMPLETED: 'try_on_completed',
    TRY_ON_FAILED: 'try_on_failed',
    TRY_ON_RESULT_DOWNLOADED: 'try_on_result_downloaded',
    TRY_ON_RESULT_SHARED: 'try_on_result_shared',
    TRY_ON_LIMIT_REACHED: 'try_on_limit_reached',

    // Product Management
    PRODUCT_IMPORT_STARTED: 'product_import_started',
    PRODUCT_IMPORT_COMPLETED: 'product_import_completed',
    PRODUCT_SAVED: 'product_saved',
    PRODUCT_UNSAVED: 'product_unsaved',

    // Product Viewing & Interaction
    PRODUCT_PAGE_VIEWED: 'product_page_viewed',
    PRODUCT_PAGE_EXITED: 'product_page_exited',
    PRODUCT_CARD_CLICKED: 'product_card_clicked',
    PRODUCT_CARD_VISIBLE: 'product_card_visible',
    PRODUCT_BUY_NOW_CLICKED: 'product_buy_now_clicked',
    PRODUCT_TRY_ON_CLICKED: 'product_try_on_clicked',
    PRODUCT_SHARED: 'product_shared',
    PRODUCT_OPTIONS_OPENED: 'product_options_opened',
    PRODUCT_EXTERNAL_LINK_CLICKED: 'product_external_link_clicked',

    // Collections
    COLLECTION_SAVED: 'collection_saved',
    COLLECTION_UNSAVED: 'collection_unsaved',
    COLLECTION_PAGE_VIEWED: 'collection_page_viewed',
    COLLECTION_PAGE_EXITED: 'collection_page_exited',
    COLLECTION_CARD_CLICKED: 'collection_card_clicked',
    COLLECTION_TAB_SWITCHED: 'collection_tab_switched',
    COLLECTION_PRODUCT_CLICKED: 'collection_product_clicked',
    COLLECTION_SHARED: 'collection_shared',
    COLLECTION_OPTIONS_OPENED: 'collection_options_opened',

    // Save to Collection Flow
    SAVE_TO_COLLECTION_OPENED: 'save_to_collection_opened',
    SAVE_TO_COLLECTION_SEARCHED: 'save_to_collection_searched',
    SAVE_TO_COLLECTION_NEW_CREATED: 'save_to_collection_new_created',

    // Search & Discovery
    SEARCH_INITIATED: 'search_initiated',
    SEARCH_QUERY_SUBMITTED: 'search_query_submitted',
    SEARCH_TAB_CHANGED: 'search_tab_changed',
    SEARCH_RESULTS_VIEWED: 'search_results_viewed',
    SEARCH_RESULT_CLICKED: 'search_result_clicked',

    // Filters
    FILTER_PANEL_OPENED: 'filter_panel_opened',
    FILTER_PANEL_CLOSED: 'filter_panel_closed',
    FILTER_APPLIED: 'filter_applied',
    FILTER_CLEARED: 'filter_cleared',
    CURRENCY_CHANGED: 'currency_changed',
    GENDER_TOGGLE_CHANGED: 'gender_toggle_changed',

    // Extension
    EXTENSION_DOWNLOAD_CLICKED: 'extension_download_clicked',

    // Recommendations
    RECOMMENDATION_IMPRESSION: 'recommendation_impression',

    // Profiles
    PROFILE_CARD_CLICKED: 'profile_card_clicked',

    // Stories
    STORIES_TRAY_VIEWED: 'stories_tray_viewed',
    STORY_OPENED: 'story_opened',
    STORY_PRODUCT_VIEWED: 'story_product_viewed',
    STORY_PRODUCT_TAPPED: 'story_product_tapped',
    STORY_COLLECTION_TAPPED: 'story_collection_tapped',
    STORY_PRODUCT_SAVED: 'story_product_saved',
    STORY_COMPLETED: 'story_completed',
    STORY_CLOSED: 'story_closed',
    STORY_NAVIGATION: 'story_navigation',
    YOUR_STORY_CLICKED: 'your_story_clicked',
    YOUR_STORY_INFO_VIEWED: 'your_story_info_viewed',
} as const

/**
 * Type for analytics event names
 */
export type AnalyticsEventName =
    (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents]

/**
 * Context builder utilities for different access patterns
 */
export const AnalyticsContext = {
    /**
     * Build context for home feed - following tab
     * @param feedItemIndex Index of the feed item (e.g., which activity)
     * @param activityItemIndex Index of item within the feed item (e.g., which product in the activity)
     * @param section Section within the feed ('feed' or 'recommendations')
     * @param recommendationStrategy Optional recommendation strategy
     * @param recommendationId Optional recommendation ID for A/B testing
     */
    followingFeed: (
        feedItemIndex: number,
        activityItemIndex: number,
        section: 'feed' | 'recommendations' = 'feed',
        recommendationStrategy?: EventContext['recommendation_strategy'],
        recommendationId?: string
    ): EventContext => ({
        feed_type: 'following',
        page_type: 'home',
        recommendation_strategy: recommendationStrategy || 'none',
        recommendation_id: recommendationId,
        feed_item_index: feedItemIndex,
        activity_item_index: activityItemIndex,
        section,
    }),

    /**
     * Build context for home feed - discover/products tab
     */
    discoverFeed: (
        position: number,
        recommendationStrategy?: EventContext['recommendation_strategy'],
        recommendationId?: string
    ): EventContext => ({
        feed_type: 'discover',
        page_type: 'home',
        recommendation_strategy: recommendationStrategy || 'none',
        recommendation_id: recommendationId,
        position,
    }),

    /**
     * Build context for search results
     */
    searchResults: (
        searchQuery: string,
        position: number,
        recommendationStrategy: EventContext['recommendation_strategy'] = 'search-based'
    ): EventContext => ({
        feed_type: 'search',
        page_type: 'search',
        recommendation_strategy: recommendationStrategy,
        search_query: searchQuery,
        position,
    }),

    /**
     * Build context for collection page
     */
    collectionPage: (
        collectionId: string,
        section:
            | 'collection_products'
            | 'recommendations' = 'collection_products',
        recommendationStrategy?: EventContext['recommendation_strategy'],
        position?: number
    ): EventContext => ({
        feed_type: 'none',
        page_type: 'collection',
        collection_id: collectionId,
        recommendation_strategy: recommendationStrategy || 'none',
        section,
        position,
    }),

    /**
     * Build context for profile page
     */
    profilePage: (
        profileId: string,
        section:
            | 'profile_collections'
            | 'profile_products'
            | 'recommendations' = 'profile_collections',
        recommendationStrategy?: EventContext['recommendation_strategy'],
        position?: number
    ): EventContext => ({
        feed_type: 'none',
        page_type: 'profile',
        profile_id: profileId,
        recommendation_strategy: recommendationStrategy || 'none',
        section,
        position,
    }),

    /**
     * Build context for product page - similar products section
     */
    productPageSimilar: (
        recommendationStrategy: EventContext['recommendation_strategy'],
        position: number,
        recommendationId?: string
    ): EventContext => ({
        feed_type: 'none',
        page_type: 'product',
        recommendation_strategy: recommendationStrategy,
        recommendation_id: recommendationId,
        section: 'similar_products',
        position,
    }),

    /**
     * Build context for story viewer
     */
    storyViewer: (
        userIndex: number,
        productIndex: number,
        userId: string
    ): EventContext => ({
        feed_type: 'following',
        page_type: 'home',
        section: 'stories',
        feed_item_index: userIndex,
        activity_item_index: productIndex,
        profile_id: userId,
    }),
}
